import React, { useState } from 'react';
import './Science.css';

const Science = () => {
  const [activeTab, setActiveTab] = useState('simple');

  const scienceContent = {
    simple: {
      title: 'Simplicity is the key',
      content: (
          <>
          <p>Achieving a calorie deficit is the foundation of weight loss—about <b className="highlight-text">3,500 calories burned equals roughly one pound of fat lost</b><sup>1</sup>.
          Research consistently shows that people who track what they eat are far more likely to lose weight and keep it off <sup>2</sup>.
          But tracking only works when it's simple and sustainable.</p>
          <br></br>
          <p><b className="highlight-text">SlimBuddy</b> was built around that principle. It uses <b className="highlight-text">AI-powered food recognition</b> and <b className="highlight-text">Apple Health integration</b> to track both calories in and calories out—effortlessly. No clutter, no overwhelm, just the essentials you need to stay consistent.</p>
          <br></br>
          <p>Because at the end of the day, <b className="highlight-text">consistency beats complexity</b>. The easier it is to log your meals and workouts, the more likely you are to stay in your calorie deficit—and the faster you'll see results. SlimBuddy makes healthy progress simple.</p>
          <p>__________</p>
          <p className="reference"><sup>1</sup> <a href="https://pmc.ncbi.nlm.nih.gov/articles/PMC8017325/" className="reference-link">Kim, J.Y., 2020. Optimal diet strategies for weight loss and weight loss maintenance. Journal of obesity & metabolic syndrome, 30(1), p.20.</a></p>
          <p className="reference"><sup>2</sup> <a href="https://pmc.ncbi.nlm.nih.gov/articles/PMC5568610/" className="reference-link">Ingels, J.S. et al. 2017. The effect of adherence to dietary tracking on weight loss: using HLM to model weight loss over time. Journal of diabetes research, 2017(1), p.6951495. </a></p>
          </>
      ),
      stats: [
        { number: '3,500', label: 'Calories = 1lb weight loss' },
      ]
    },
    protein: {
      title: 'Protein to fuel muscle growth',
      content: (
        <>
        <p>When you're cutting calories to lose fat, keeping your muscles intact is just as important as losing weight. 
        Research shows that eating more than the basic minimum—<b className="highlight-text">roughly 0.45–0.75 g of protein per pound of body weight</b>—helps preserve lean muscle mass during weight loss. <sup>1,2</sup></p>
        <br></br>
        <p>That’s why <b className="highlight-text">SlimBuddy</b> doesn’t just track calories and macros — it <b className="highlight-text">integrates your protein goals seamlessly</b>. 
        With a minimalistic design and powerful AI, you log your meals, keep an eye on your protein intake alongside calories and macros, and stay connected to your workouts through Apple Health.
        The result: an effortless system that supports muscle preservation while you pursue fat-loss.</p>
        <br></br>
        <p>Simpler tracking. Smarter nutrition. With SlimBuddy in your corner, you’re not only protecting your muscle — you’re <b className="highlight-text">building lasting results</b>.</p>
        <p>__________</p>
          <p className="reference"><sup>1</sup> <a href="https://pmc.ncbi.nlm.nih.gov/articles/PMC10552824/" className="reference-link">Janssen, T.A.H. et al., 2023. The impact and utility of very low-calorie diets: the role of exercise and protein in preserving skeletal muscle mass. Current Opinion in Clinical Nutrition & Metabolic Care, 26(6), pp.521-527 </a></p>
          <p className="reference"><sup>2</sup> <a href="https://onlinelibrary.wiley.com/doi/10.1002/jcsm.12922" className="reference-link">Nunes, E.A. et al., 2022. Systematic review and meta‐analysis of protein intake to support muscle mass and function in healthy adults. Journal of cachexia, sarcopenia and muscle, 13(2), pp.795-810.</a></p>
        </>  
      ),
      stats: [
        { number: '>0.7 gr/lb', label: 'of protein for muscle retention and growth' },
      ]
    },
    fat: {
      title: 'Healthy amounts of fat',
      content: (
        <>
        <p>Healthy hormone levels require more than calories and protein—they also rely on sufficient dietary fat. 
        Fat isn’t just extra energy — your body uses it as a key building block for steroid hormones like testosterone, estradiol, and cortisol. 
        Studies show that very <b className="highlight-text">low-fat diets can reduce testosterone levels.</b><sup>1,2</sup> </p>
        <br></br>
        <p>A practical rule of thumb: aim for <b className="highlight-text">about 0.35-0.4 g of fat</b> per lb of body weight to support hormone balance and keep your endocrine system functioning smoothly.</p>
        <br></br>
        <p>With <b className="highlight-text">SlimBuddy</b>, tracking fat intake becomes effortless—just log your meals (or use AI to recognise them), and monitor calories, 
          macros and healthy fat targets alongside your workouts via Apple Health. </p>
        <br></br>
        <p><b className="highlight-text">Minimal fuss. Maximum support for both your physique and your hormones.</b></p>
        <p>__________</p>
          <p className="reference"><sup>1</sup> <a href="https://pubmed.ncbi.nlm.nih.gov/33741447/" className="reference-link">Whittaker, J. and Wu, K., 2021. Low-fat diets and testosterone in men: Systematic review and meta-analysis of intervention studies. The Journal of steroid biochemistry and molecular biology, 210, p.105878. </a></p>
          <p className="reference"><sup>2</sup> <a href="https://pubmed.ncbi.nlm.nih.gov/26843151/" className="reference-link">Mumford, S.L. et al., 2016. Dietary fat intake and reproductive hormone concentrations and ovulation in regularly menstruating women. The American journal of clinical nutrition, 103(3), pp.868-877.</a></p>
        </>
      ),
      stats: [
        { number: '0.35-0.4 gr/lb', label: 'of fat for healthy hormonal levels' },
      ]
    }
  };

  return (
    <section className="science" id="science">
      <div className="container">
        <div className="science-header">
          <h2>The Science Behind SlimBuddy</h2>
          <p>Evidence-based approach backed by nutrition science and behavioral psychology</p>
        </div>

        <div className="science-tabs">
          <button 
            className={`tab-btn ${activeTab === 'simple' ? 'active' : ''}`}
            onClick={() => setActiveTab('simple')}
          >
            Simplicity
          </button>
          <button 
            className={`tab-btn ${activeTab === 'protein' ? 'active' : ''}`}
            onClick={() => setActiveTab('protein')}
          >
            Protein rich
          </button>
          <button 
            className={`tab-btn ${activeTab === 'fat' ? 'active' : ''}`}
            onClick={() => setActiveTab('fat')}
          >
            Healthy Fat
          </button>
        </div>

        <div className="science-content">
          <div className="science-text">
            <h3>{scienceContent[activeTab].title}</h3>
            <p>{typeof scienceContent[activeTab].content === 'string' ? scienceContent[activeTab].content : scienceContent[activeTab].content}</p>
          </div>
          
          <div className="science-stats">
            {scienceContent[activeTab].stats.map((stat, index) => (
              <div key={index} className="science-stat">
                <span className="stat-number">{stat.number}</span>
                <span className="stat-label">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="research-note">
          <p>
            <strong>Research-Backed:</strong> Our algorithms are based on peer-reviewed studies 
            from leading nutrition and exercise science journals, continuously updated with the 
            latest research findings.
          </p>
        </div>
      </div>
    </section>
  );
};

export default Science;